import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { PIPELINE_FLOW, STAGE_META } from '../../lib/workflow';
import { WeekChart } from '../workflow/WeekChart';
import { HomeHeader } from './HomeHeader';
import { NayanHome } from './NayanHome';
import { NiveditHome } from './NiveditHome';
import { UtkarshHome } from './UtkarshHome';
import Dashboard from '../../pages/Dashboard';

const PipelineFlow = ({ counts }) => {
  const navigate = useNavigate();
  return (
    <div className="wf-card p-5 sm:p-6" data-testid="founder-pipeline-flow">
      <h2 className="font-jakarta text-lg font-bold text-[#2D2623] mb-4">🌊 Listing pipeline</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {PIPELINE_FLOW.map((s, i) => (
          <button
            key={s}
            onClick={() => navigate(`/etsy?stage=${s}`)}
            className={`wf-rise relative rounded-2xl border p-4 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-md ${STAGE_META[s].cls}`}
            style={{ animationDelay: `${i * 50}ms` }}
            data-testid={`founder-stage-tile-${s}`}
          >
            <div className="text-2xl" aria-hidden>{STAGE_META[s].emoji}</div>
            <div className="text-3xl font-extrabold mt-1" data-testid={`founder-stage-count-${s}`}>{counts?.[s] ?? 0}</div>
            <div className="text-[11px] font-bold uppercase tracking-wide opacity-80">{STAGE_META[s].label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const Person = ({ emoji, name, stats, testId }) => (
  <div className="wf-card p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="w-11 h-11 rounded-2xl bg-[#F7F3EC] flex items-center justify-center text-2xl" aria-hidden>{emoji}</span>
      <h3 className="font-jakarta text-base font-bold text-[#2D2623]">{name}</h3>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {stats.map(([e, v, l]) => (
        <div key={l} className="rounded-xl bg-[#FDFBF7] border border-[#F0EAE1] px-3 py-2">
          <div className="text-lg font-extrabold text-[#2D2623]">{e} {v}</div>
          <div className="text-[10px] uppercase tracking-wide font-semibold text-[#9A8E85]">{l}</div>
        </div>
      ))}
    </div>
  </div>
);

export const FounderHome = ({ data, user }) => {
  const { summary } = data;
  const t = summary?.today || {};
  return (
    <div className="space-y-8" data-testid="founder-home">
      <HomeHeader emoji="👑" name={user?.name} subtitle="Here's the whole listing machine at a glance. Tap a stage to open it in Listings." />
      <PipelineFlow counts={summary?.counts} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Person emoji="👨‍💻" name="Nayan" testId="founder-team-nayan" stats={[['🔗', `${t.submitted || 0}/${summary?.target || 20}`, 'Links today'], ['🛍️', t.marked_live || 0, 'Live on Etsy'], ['✅', t.approved || 0, 'Approved'], ['🔁', t.changes_requested || 0, 'Changes sent']]} />
          <Person emoji="📸" name="Nivedit" testId="founder-team-nivedit" stats={[['🚀', t.sent_for_review || 0, 'Sent for review'], ['🎨', summary?.counts?.in_visuals || 0, 'In visuals now'], ['🔗', summary?.counts?.new || 0, 'Untouched links'], ['🔁', summary?.counts?.changes_requested || 0, 'To fix']]} />
          <Person emoji="🌐" name="Utkarsh" testId="founder-team-utkarsh" stats={[['✅', t.channel_done || 0, 'Channels ticked'], ['🎉', t.completed || 0, 'Completed'], ['🛍️', summary?.counts?.live_on_etsy || 0, 'In his queue'], ['🏁', summary?.counts?.done || 0, 'All-time done']]} />
        </div>
        <div className="lg:col-span-4 wf-card p-5">
          <h3 className="font-jakarta text-base font-bold text-[#2D2623] mb-2">📈 Last 7 days</h3>
          <WeekChart
            series={summary?.series} height={170} testId="founder-week-chart"
            bars={[
              { key: 'submitted', label: 'Links', color: '#E07A5F' },
              { key: 'sent_for_review', label: 'Reviews', color: '#A855F7' },
              { key: 'marked_live', label: 'Live', color: '#81B29A' },
            ]}
          />
        </div>
      </div>
      <Tabs defaultValue="nayan" className="space-y-6">
        <TabsList className="bg-[#F4EFE6] rounded-2xl p-1 h-auto flex-wrap">
          {[['nayan', "👨‍💻 Nayan's view"], ['nivedit', "📸 Nivedit's view"], ['utkarsh', "🌐 Utkarsh's view"], ['content', '✨ Content & ideas']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-xl px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid={`role-selector-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="nayan"><NayanHome data={data} user={user} embedded /></TabsContent>
        <TabsContent value="nivedit"><NiveditHome data={data} user={user} embedded /></TabsContent>
        <TabsContent value="utkarsh"><UtkarshHome data={data} user={user} embedded /></TabsContent>
        <TabsContent value="content"><Dashboard embedded /></TabsContent>
      </Tabs>
    </div>
  );
};
